import { useState, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import logo from '../../assets/logos/saccos_logo.png';
import {
  LayoutDashboard,
  Home,
  FileText,
  Info,
  Mail,
  CreditCard,
  PiggyBank,
  Landmark,
  Newspaper,
  Image,
  PanelTop,
  HeadphonesIcon,
  Menu as MenuIcon,
  BookOpen,
  ChevronDown,
  ChevronRight,
  LogOut,
  X,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import GlobalLoader from './GlobalLoader';
import { clsx } from 'clsx';

const navGroups = [
  {
    label: 'Main',
    items: [
      { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    ],
  },
  {
    label: 'Pages Management',
    items: [
      { path: '/pages/home', label: 'Home Page', icon: Home },
      { path: '/pages/about-us', label: 'About Us', icon: Info },
      { path: '/pages/contact', label: 'Contact', icon: Mail },
      { path: '/pages/organization-structure', label: 'Organization Structure', icon: Landmark },
    ],
  },
  {
    label: 'Products',
    items: [
      { path: '/products/savings-shares', label: 'Savings & Shares', icon: PiggyBank },
      { path: '/products/loans', label: 'Loan Products', icon: CreditCard },
    ],
  },
  {
    label: 'Media Centre',
    items: [
      { path: '/media-centre', label: 'Media & Publications', icon: Newspaper },
    ],
  },
  {
    label: 'Media & Layout',
    items: [
      { path: '/media-library', label: 'Media Library', icon: Image },
      { path: '/top-header', label: 'Top-Header Mngt', icon: PanelTop, permission: 'edit' },
      { path: '/menus', label: 'Menus Management', icon: MenuIcon, permission: 'edit' },
      { path: '/feedback', label: 'Feedback Management', icon: HeadphonesIcon },
      { path: '/footer', label: 'Footer Management', icon: BookOpen, permission: 'edit' },
    ],
  },
];

export default function Sidebar() {
  const location = useLocation();
  const { logout, hasPermission } = useAuth();
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const visibleGroups = useMemo(() => {
    return navGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => !item.permission || hasPermission(item.permission)),
      }))
      .filter((group) => group.items.length > 0);
  }, [hasPermission]);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
    } catch {
      setLoggingOut(false);
    }
  };

  const toggleGroup = (label) => {
    setCollapsedGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const isActive = (path) => location.pathname.startsWith(path);

  return (
    <>
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-md shadow-md"
      >
        {mobileOpen ? <X className="w-5 h-5" /> : <MenuIcon className="w-5 h-5" />}
      </button>

      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        className={clsx(
          'fixed lg:static inset-y-0 left-0 z-40 w-72 bg-brand-navy flex flex-col transition-transform duration-300',
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
      >
        <div className="h-20 flex items-center px-4 border-b border-brand-navy-light">
          <img src={logo} alt="Uhamiaji SACCOS" className="h-14 w-auto" />
          <h1 className="ml-3 text-2xl font-bold text-white">Uhamiaji CMS</h1>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {visibleGroups.map((group) => {
            const collapsed = !!collapsedGroups[group.label];
            const hasActive = group.items.some((item) => isActive(item.path));
            const showItems = !collapsed || hasActive;

            return (
              <div key={group.label} className="mb-2">
                <button
                  onClick={() => toggleGroup(group.label)}
                  className="w-full flex items-center justify-between px-3 py-2 text-[13px] font-semibold text-brand-cream/60 uppercase tracking-wider hover:text-brand-cream"
                >
                  {group.label}
                  {collapsed && !hasActive ? (
                    <ChevronRight className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </button>

                {showItems && (
                  <div className="space-y-1 mt-1">
                    {group.items.map((item) => {
                      const Icon = item.icon;
                      const active = isActive(item.path);
                      return (
                        <NavLink
                          key={item.path}
                          to={item.path}
                          onClick={() => setMobileOpen(false)}
                          className={clsx(
                            'group flex items-center px-3 py-2.5 rounded-md text-[15px] font-medium transition-colors relative',
                            active
                              ? 'bg-brand-navy-light text-white'
                              : 'text-brand-cream/70 hover:bg-brand-navy-light/60 hover:text-white'
                          )}
                        >
                          {active && <span className="absolute left-0 top-1.5 bottom-1.5 w-1 bg-brand-gold rounded-r-full" />}
                          <Icon className={clsx('w-5 h-5 mr-3 flex-shrink-0', active ? 'text-brand-gold' : 'text-brand-cream/60 group-hover:text-white')} />
                          {item.label}
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="border-t border-brand-navy-light px-3 py-2">
          <button
            onClick={handleLogout}
            disabled={loggingOut}
            className="flex items-center w-full px-3 py-2.5 text-[15px] font-medium text-red-600 rounded-md hover:bg-red-50 disabled:opacity-50"
          >
            <LogOut className="w-5 h-5 mr-3" />
            {loggingOut ? 'Logging out…' : 'Log Out'}
          </button>
        </div>
      </aside>
      {loggingOut && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <GlobalLoader size="lg" message="Logging out…" />
        </div>
      )}
    </>
  );
}
